import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { ArrowForward as ArrowIcon } from '@mui/icons-material';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEOHead';
import api from '../config/api';
import { mapApiShowToEvent, SHOWS_HUB } from '../config/marketingEvents';

/** Approximate sticky Navbar (logo bar + secondary tabs) */
const NAV_OFFSET = { xs: '132px', md: '148px' };

const panelCtaSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  textTransform: 'none',
  fontWeight: 700,
  px: { xs: 2, md: 2.5 },
  py: { xs: 0.85, md: 1 },
  borderRadius: 2,
  bgcolor: 'rgba(255,255,255,0.16)',
  color: '#fff',
  backdropFilter: 'blur(6px)',
  fontSize: { xs: '0.85rem', md: '0.9rem' },
  transition: 'background-color 0.25s ease, color 0.25s ease',
};

/**
 * Trade-show hub: one or more active shows from admin config.
 * Sized to one viewport (below the sticky navbar) — no page scroll.
 */
const WTMHub = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [hub, setHub] = useState(null);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/marketing/hub');
        if (cancelled) return;
        setHub(data?.hub || null);
        setEvents((data?.shows || []).map(mapApiShowToEvent).filter(Boolean));
      } catch {
        if (!cancelled) {
          setHub(null);
          setEvents([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loading && events.length === 1) {
    return <Navigate to={events[0].path} replace />;
  }

  const seoTitle = hub?.seoTitle || t('eventPages.hub.seo.title');
  const seoDescription = hub?.seoDescription || t('eventPages.hub.seo.description');
  const seoKeywords = hub?.seoKeywords || t('eventPages.hub.seo.keywords');
  const multi = events.length > 1;

  return (
    <Box
      sx={{
        bgcolor: '#111',
        color: '#fff',
        height: {
          xs: `calc(100dvh - ${NAV_OFFSET.xs})`,
          md: `calc(100dvh - ${NAV_OFFSET.md})`,
        },
        maxHeight: {
          xs: `calc(100dvh - ${NAV_OFFSET.xs})`,
          md: `calc(100dvh - ${NAV_OFFSET.md})`,
        },
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={`https://dejavunomad.com${SHOWS_HUB.path}`}
      />

      <Box
        sx={{
          flexShrink: 0,
          textAlign: 'center',
          pt: { xs: 1.5, md: 2 },
          pb: { xs: 1, md: 1.5 },
          px: 2,
        }}
      >
        <Typography
          variant="overline"
          sx={{
            letterSpacing: 2.5,
            fontWeight: 700,
            opacity: 0.85,
            fontSize: { xs: '0.65rem', md: '0.7rem' },
            lineHeight: 1.2,
          }}
        >
          {hub?.eyebrow || t('eventPages.hub.eyebrow')}
        </Typography>
        <Typography
          component="h1"
          sx={{
            fontFamily: 'var(--font-display), Playfair Display, serif',
            fontWeight: 700,
            fontSize: { xs: '1.45rem', sm: '1.75rem', md: '2.1rem' },
            mt: 0.25,
            mb: 0.5,
            lineHeight: 1.15,
          }}
        >
          {hub?.title || t('eventPages.hub.title')}
        </Typography>
        <Typography
          sx={{
            maxWidth: 520,
            mx: 'auto',
            opacity: 0.88,
            fontSize: { xs: '0.8rem', md: '0.9rem' },
            lineHeight: 1.4,
          }}
        >
          {hub?.subtitle || t('eventPages.hub.subtitle')}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: multi ? `repeat(${Math.min(events.length, 2)}, 1fr)` : '1fr',
          },
          gridTemplateRows: { xs: multi ? '1fr 1fr' : '1fr', md: '1fr' },
          position: 'relative',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: '#E8C547' }} />
          </Box>
        ) : events.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3 }}>
            <Typography sx={{ opacity: 0.8, textAlign: 'center' }}>
              {t('eventPages.hub.subtitle')}
            </Typography>
          </Box>
        ) : (
          <>
            {multi ? (
              <Box
                aria-hidden
                sx={{
                  display: { xs: 'none', md: 'block' },
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: '50%',
                  width: 3,
                  transform: 'translateX(-50%) skewX(-8deg)',
                  bgcolor: 'rgba(255,255,255,0.35)',
                  zIndex: 3,
                  pointerEvents: 'none',
                  boxShadow: '0 0 24px rgba(0,0,0,0.35)',
                }}
              />
            ) : null}

            {events.map((event, index) => (
              <Box
                key={event.id}
                component={RouterLink}
                to={event.path}
                sx={{
                  position: 'relative',
                  minHeight: 0,
                  height: '100%',
                  textDecoration: 'none',
                  color: 'inherit',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'flex-end',
                  backgroundImage: `linear-gradient(180deg, rgba(17,17,17,0.1) 0%, rgba(17,17,17,0.45) 40%, rgba(17,17,17,0.9) 100%), url(${event.splitImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  transition: 'filter 0.3s ease',
                  '&:hover': {
                    filter: { md: 'brightness(1.08)' },
                    zIndex: 2,
                    '& .wtm-hub-cta': {
                      bgcolor: '#fff',
                      color: '#591716',
                    },
                  },
                  '&:focus-visible': {
                    outline: '3px solid #fff',
                    outlineOffset: -3,
                    zIndex: 4,
                  },
                  clipPath: {
                    xs: 'none',
                    md:
                      multi && index === 0
                        ? 'polygon(0 0, calc(100% - 24px) 0, 100% 100%, 0 100%)'
                        : multi && index === 1
                          ? 'polygon(24px 0, 100% 0, 100% 100%, 0 100%)'
                          : 'none',
                  },
                  marginLeft: { md: multi && index === 1 ? '-24px' : 0 },
                  marginRight: { md: multi && index === 0 ? '-24px' : 0 },
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    width: '100%',
                    py: { xs: 2, md: 3 },
                    px: { xs: 2.5, md: multi && index === 0 ? 3 : 5 },
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      letterSpacing: 1.8,
                      fontWeight: 700,
                      opacity: 0.9,
                      fontSize: { xs: '0.65rem', md: '0.7rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    {[event.name, event.dates].filter(Boolean).join(' · ')}
                  </Typography>
                  <Typography
                    component="h2"
                    sx={{
                      fontFamily: 'var(--font-display), Playfair Display, serif',
                      fontWeight: 700,
                      fontSize: { xs: '1.35rem', sm: '1.6rem', md: '2rem' },
                      lineHeight: 1.15,
                      mb: 0.5,
                    }}
                  >
                    {event.hubTitle}
                  </Typography>
                  <Typography
                    sx={{
                      opacity: 0.9,
                      mb: 0.75,
                      maxWidth: 360,
                      lineHeight: 1.4,
                      fontSize: { xs: '0.8rem', md: '0.9rem' },
                      display: { xs: '-webkit-box', md: 'block' },
                      WebkitLineClamp: { xs: 2, md: 'unset' },
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {event.hubSubtitle}
                  </Typography>
                  {(event.dates || event.venue) && (
                    <Typography
                      variant="body2"
                      sx={{
                        opacity: 0.75,
                        mb: 1.5,
                        fontWeight: 500,
                        fontSize: { xs: '0.75rem', md: '0.8rem' },
                      }}
                    >
                      {[event.dates, event.venue].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                  <Box className="wtm-hub-cta" component="span" sx={panelCtaSx}>
                    {event.hubCta}
                    <ArrowIcon sx={{ fontSize: 16 }} />
                  </Box>
                </Box>
              </Box>
            ))}
          </>
        )}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          py: { xs: 1, md: 1.25 },
          px: 2,
          textAlign: 'center',
          bgcolor: '#111',
          borderTop: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          justifyContent="center"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Typography
            variant="body2"
            sx={{ opacity: 0.7, fontSize: { xs: '0.75rem', md: '0.8rem' } }}
          >
            {t('eventPages.hub.footerNote')}
          </Typography>
          <Chip
            size="small"
            label={t('eventPages.hub.promo')}
            sx={{
              background: 'linear-gradient(180deg, #F5E6B8 0%, #D4AF37 48%, #B8860B 100%)',
              color: '#3D2A00',
              fontWeight: 700,
              fontSize: { xs: '0.7rem', md: '0.75rem' },
              border: '1px solid #E8C547',
              boxShadow: '0 0 12px rgba(212, 175, 55, 0.45)',
            }}
          />
          <Button
            component={RouterLink}
            to="/partner"
            size="small"
            sx={{
              textTransform: 'none',
              color: '#fff',
              fontWeight: 600,
              fontSize: { xs: '0.75rem', md: '0.8rem' },
              minHeight: 0,
              py: 0.25,
            }}
          >
            {t('eventPages.hub.partnerLink')}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default WTMHub;
